import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PlanElement, produksjonAPI } from '@/services/api';
import type { Musikk, MusikkTransponering } from '@/services/api/types';
import { calculateTotalDuration, calculateStartTime } from '@/lib/timeCalculations';
import { usePlanElementForm } from '@/hooks/usePlanElementForm';
import { usePlanElements, usePlanDragDrop } from './plan-editor/hooks';
import { PlanEditorHeader, PlanElementList } from './plan-editor/components';
import PlanElementDialog from './PlanElementDialog';
import SongSearchDialog from './SongSearchDialog';

/**
 * PlanEditor - Container component for production plan editing
 * 
 * Refactored from 916 lines to ~200 lines by extracting:
 * - Custom hooks: usePlanElements, usePlanDragDrop
 * - Sub-components: PlanEditorHeader, PlanElementList, SortableOverskriftRow, SortableHendelseRow
 * 
 * Responsibilities:
 * - Coordinate data fetching and state management (via hooks)
 * - Manage dialog state and form interactions
 * - Provide callbacks to child components
 * - Display loading states and notifications
 */

interface Props {
  produksjonId: number;
  onSave?: () => void;
  readOnly?: boolean;
}

const PlanEditor: React.FC<Props> = ({ produksjonId, onSave, readOnly = false }) => {
  // Data management via custom hook
  const {
    elements,
    produksjon,
    loading,
    error,
    success,
    fetchData,
    createElement,
    updateElement,
    deleteElement,
    updateMultipleRekkefølge,
    setElementsOptimistic,
    clearError,
    clearSuccess,
    setError,
    setSuccess,
  } = usePlanElements({ produksjonId, onSave });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'overskrift' | 'hendelse' | 'sang'>('overskrift');
  const [editingElement, setEditingElement] = useState<PlanElement | null>(null);
  const [currentParentId, setCurrentParentId] = useState<number | null>(null);
  const [selectedTransponeringId, setSelectedTransponeringId] = useState<number | null>(null);
  const [sangVarighetMinutter, setSangVarighetMinutter] = useState<number>(0);
  
  // Song search dialog state
  const [songSearchOpen, setSongSearchOpen] = useState(false);
  const [selectedParentForSong, setSelectedParentForSong] = useState<number | null>(null);
  const [placeholderToConvert, setPlaceholderToConvert] = useState<PlanElement | null>(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState<{ id: number; navn: string; type: string } | null>(null);
  
  // Form state (via custom hook)
  const formState = usePlanElementForm();

  // Initialize sang-specific state when editing a song
  useEffect(() => {
    if (editingElement && editingElement.type === 'sang') {
      // Initialize transponering
      setSelectedTransponeringId(editingElement.musikk_transponering_id || null);
      
      // Initialize varighet (rund opp som i backend)
      const defaultMinutter = editingElement.musikk_lengde_minutter || 0;
      const defaultSekunder = editingElement.musikk_lengde_sekunder || 0;
      const roundedMinutter = defaultSekunder > 0 ? defaultMinutter + 1 : defaultMinutter;
      const actualVarighetMinutter = editingElement.varighet_minutter || roundedMinutter;
      setSangVarighetMinutter(actualVarighetMinutter);
    }
  }, [editingElement]);
  
  // Expandable overskrifter - starter kollapset (eller ekspandert hvis readOnly)
  const [expandedHeaders, setExpandedHeaders] = useState<Set<number>>(new Set());

  // Helper to expand a header
  const expandHeader = useCallback((headerId: number) => {
    setExpandedHeaders(prev => {
      const newSet = new Set(prev);
      newSet.add(headerId);
      return newSet;
    });
  }, []);

  // Auto-expand all headers for read-only users
  useEffect(() => {
    if (readOnly && elements.length > 0) {
      const overskriftIds = elements
        .filter(el => el.type === 'overskrift')
        .map(el => el.id);
      if (overskriftIds.length > 0) {
        setExpandedHeaders(new Set(overskriftIds));
      }
    }
  }, [readOnly, elements]);

  // Drag-and-drop via custom hook
  const {
    sensors,
    activeElement,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = usePlanDragDrop({
    elements,
    setElementsOptimistic,
    updateMultipleRekkefølge,
    updateElement,
    onSuccess: onSave,
    setSuccess,
    setError,
    expandHeader,
    readOnly,
  });

  // Fetch data on mount
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produksjonId]);

  // Toggle header expanded/collapsed
  const toggleHeader = useCallback((headerId: number) => {
    setExpandedHeaders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(headerId)) {
        newSet.delete(headerId);
      } else {
        newSet.add(headerId);
      }
      return newSet;
    });
  }, []);

  // Calculate start time for an element
  const calculateStartTimeForElement = useCallback((elementId: number): Date | null => {
    if (!produksjon?.tid) return null;
    return calculateStartTime(elementId, {
      produksjonStart: new Date(produksjon.tid),
      elements
    });
  }, [produksjon, elements]);

  // Get next rekkefølge for new element
  const getNextRekkefølge = useCallback((parentId: number | null): number => {
    const siblings = elements.filter(el => {
      if (parentId === null) {
        return el.type === 'overskrift';
      }
      return el.parent_id === parentId;
    });
    return siblings.length;
  }, [elements]);

  // Get hendelser for an overskrift
  const getHendelserForOverskrift = useCallback((overskriftId: number): PlanElement[] => {
    return elements.filter(el => el.parent_id === overskriftId);
  }, [elements]);

  // Dialog handlers
  const openOverskriftDialog = useCallback(() => {
    setDialogMode('overskrift');
    setCurrentParentId(null);
    setEditingElement(null);
    formState.resetForm();
    setDialogOpen(true);
    setTimeout(() => formState.navnInputRef.current?.focus(), 100);
  }, [formState]);

  const openHendelseDialog = useCallback((parentId: number) => {
    setDialogMode('hendelse');
    setCurrentParentId(parentId);
    setEditingElement(null);
    formState.resetForm();
    setDialogOpen(true);
    setTimeout(() => formState.navnInputRef.current?.focus(), 100);
  }, [formState]);

  const openEditDialog = useCallback((element: PlanElement) => {
    setDialogMode(element.type as 'overskrift' | 'hendelse' | 'sang');
    setEditingElement(element);
    formState.loadFromElement(element);
    setCurrentParentId(element.parent_id ?? null);
    setDialogOpen(true);
    setTimeout(() => formState.navnInputRef.current?.focus(), 100);
  }, [formState]);

  const openSongSearchDialog = useCallback((parentId: number | null = null) => {
    setSelectedParentForSong(parentId);
    setSongSearchOpen(true);
  }, []);

  const openPlaceholderSongDialog = useCallback((placeholder: PlanElement) => {
    setPlaceholderToConvert(placeholder);
    setSongSearchOpen(true);
  }, []);

  const handleSelectSong = async (song: Musikk, transponering: MusikkTransponering | null) => {
    // Sjekk om vi konverterer en placeholder eller oppretter ny sang
    if (placeholderToConvert) {
      // Konverter placeholder til sang
      try {
        await produksjonAPI.convertPlaceholderToSang(produksjonId, placeholderToConvert.id, {
          musikkId: song.id,
          musikkTransponeringId: transponering?.id || null,
        });

        await fetchData(); // Refresh plan
        setSongSearchOpen(false);
        setPlaceholderToConvert(null);
      } catch (error) {
        console.error('Feil ved konvertering:', error);
        toast.error('Kunne ikke velge sang');
      }
      return;
    }

    // Opprette ny sang (eksisterende logikk)
    let parentId = selectedParentForSong;
    if (!parentId) {
      const firstOverskrift = elements.filter(el => el.type === 'overskrift')[0];
      if (firstOverskrift) {
        parentId = firstOverskrift.id;
        expandHeader(firstOverskrift.id);
      }
    }

    if (!parentId) {
      toast.error('Du må opprette en overskrift først');
      return;
    }

    const nextRekkefølge = getNextRekkefølge(parentId);
    const success = await createElement('sang', {
      navn: song.tittel,
      parentId,
      rekkefølge: nextRekkefølge,
      beskrivelse: '',
      musikkId: song.id,
      musikkTransponeringId: transponering?.id || null,
    });

    if (success) {
      setSongSearchOpen(false);
      setSelectedParentForSong(null);
    }
  };

  const handleSave = async (beskrivelseFromDialog?: string) => {
    if (!formState.isValid()) {
      return;
    }

    // Use beskrivelse from dialog if provided (from Quill ref), otherwise use formState
    const beskrivelse = beskrivelseFromDialog !== undefined ? beskrivelseFromDialog : formState.beskrivelse;

    if (editingElement) {
      // Update existing element
      const success = await updateElement(editingElement.id, dialogMode, {
        navn: formState.navn.trim(),
        ...(dialogMode === 'hendelse' && { varighetMinutter: formState.varighet }),
        ...(dialogMode === 'overskrift' && { førProduksjonsstart: formState.førProduksjonsstart }),
        ...(dialogMode === 'sang' && { 
          musikkTransponeringId: selectedTransponeringId,
          varighetMinutter: sangVarighetMinutter,
        }),
        beskrivelse,
      });

      if (success) {
        setDialogOpen(false);
      }
    } else {
      // Create new element
      const nextRekkefølge = getNextRekkefølge(currentParentId);
      const success = await createElement(dialogMode, {
        navn: formState.navn.trim(),
        ...(dialogMode === 'hendelse' && currentParentId !== null && { 
          varighetMinutter: formState.varighet,
          parentId: currentParentId 
        }),
        ...(dialogMode === 'overskrift' && { førProduksjonsstart: formState.førProduksjonsstart }),
        rekkefølge: nextRekkefølge,
        beskrivelse,
      });

      if (success) {
        setDialogOpen(false);
      }
    }
  };

  const handleDelete = (elementId: number) => {
    const element = elements.find(el => el.id === elementId);
    if (!element) return;
    
    setElementToDelete({
      id: element.id,
      navn: element.navn,
      type: element.type === 'overskrift' ? 'overskrift' : element.type === 'sang' ? 'sang' : 'innslag'
    });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!elementToDelete) return;
    
    await deleteElement(elementToDelete.id);
    setDeleteDialogOpen(false);
    setElementToDelete(null);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Laster plan...</div>
      </div>
    );
  }

  // Calculate derived data
  const totalDuration = calculateTotalDuration(elements);
  const overskrifter = elements
    .filter(el => el.type === 'overskrift')
    .sort((a, b) => a.rekkefølge - b.rekkefølge);
  const hasHendelser = elements.some(el => el.type === 'hendelse');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Toast notifications */}
      {error && (
        <div className="fixed top-4 right-4 z-[100] max-w-md animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-center gap-2 p-3 sm:p-4 bg-destructive/95 backdrop-blur-sm border border-destructive shadow-lg rounded-lg text-white text-sm">
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
            <span className="flex-1 font-medium">{error}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={clearError} 
              className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20 shrink-0 text-white"
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 z-[100] max-w-md animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-center gap-2 p-3 sm:p-4 bg-green-600/95 backdrop-blur-sm border border-green-500 shadow-lg rounded-lg text-white text-sm">
            <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
            <span className="flex-1 font-medium">{success}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={clearSuccess} 
              className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20 shrink-0 text-white"
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Header with title, total duration, and add buttons */}
      <PlanEditorHeader
        totalDuration={totalDuration}
        hasHendelser={hasHendelser}
        onAddOverskrift={openOverskriftDialog}
        readOnly={readOnly}
      />

      {/* Plan elements list with drag-and-drop */}
      <div className="space-y-2 sm:space-y-3">
        <PlanElementList
          elements={elements}
          overskrifter={overskrifter}
          expandedHeaders={expandedHeaders}
          activeElement={activeElement}
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          onToggleHeader={toggleHeader}
          onEdit={openEditDialog}
          onDelete={handleDelete}
          onOpenHendelseDialog={openHendelseDialog}
          onOpenSangDialog={openSongSearchDialog}
          onSelectSong={openPlaceholderSongDialog}
          calculateStartTime={calculateStartTimeForElement}
          getHendelserForOverskrift={getHendelserForOverskrift}
          readOnly={readOnly}
        />
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slett {elementToDelete?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette "{elementToDelete?.navn}"?
              {elementToDelete?.type === 'overskrift' && (
                <span className="block mt-2 text-destructive font-medium">
                  Alle innslag under denne overskriften vil også bli slettet.
                </span>
              )}
              <span className="block mt-2">
                Denne handlingen kan ikke angres.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog for add/edit */}
      <PlanElementDialog
        open={dialogOpen}
        mode={dialogMode}
        isEditing={!!editingElement}
        formState={formState}
        element={editingElement || undefined}
        onSave={handleSave}
        onCancel={() => setDialogOpen(false)}
        onTransponeringChange={setSelectedTransponeringId}
        onSangVarighetChange={setSangVarighetMinutter}
      />

      {/* Song search dialog */}
      <SongSearchDialog
        open={songSearchOpen}
        onClose={() => {
          setSongSearchOpen(false);
          setPlaceholderToConvert(null);
          setSelectedParentForSong(null);
        }}
        onSelectSong={handleSelectSong}
      />
    </div>
  );
};

export default PlanEditor;
